/**
 * activeStreamRegistry.ts — Round O (2026-07-15) false-"stopped" root fix.
 *
 * Root cause being closed: `useGlobalTools` kept ONE global `activeStreamRef`
 * holding the in-flight turn's thinking-widget finalizer, registered at
 * dispatch and — on the migrated prest0n-session lane — never cleared or
 * re-registered. `cancelRunningTool(threadId)` STEP 1 ignored the passed
 * `threadId` and finalized whatever that single slot held. Consequence:
 * archiving unrelated thread B finalized thread A's LIVE widget into a static
 * "stopped" bubble (routed into A's storage via routedFinalizeById, which also
 * released A's processing flag) while A's server task kept running — the
 * answer then "appeared out of nowhere" minutes later.
 *
 * This registry replaces the single slot with a PER-THREAD map so a cancel
 * can only ever finalize the stream registered for the thread actually being
 * cancelled. Key semantics:
 *
 *  - `threadId: null` keys the legacy/single-thread lane (no thread switcher —
 *    `originatingThreadId` is null there by construction).
 *  - `finalizeForCancel(threadId, …)` with an EXPLICIT thread id finalizes
 *    only that thread's entry. With NO id (`null`/`undefined`) it keeps the
 *    legacy "stop everything" semantics (global stop button / single-thread
 *    surfaces, where the active thread id resolves to null).
 *  - `clear(threadId, entry?)` is entry-scoped when `entry` is passed
 *    (compare-and-delete), so a stale turn's deferred cleanup can never evict
 *    a NEWER turn's registration for the same thread.
 *
 * Pure TS (no React) so the cancel-scope contract is directly unit-testable;
 * `useGlobalTools` holds one instance in a ref.
 */

export interface ActiveStreamContent {
  text: string;
  html: string;
  sourceTag: string;
}

export interface ActiveStreamEntry {
  /**
   * The ORIGINATING thread of the turn that owns this stream — the explicit
   * turn-scoped value (Round J), NEVER the last-writer-wins current-turn ref.
   * `null` = legacy/single-thread lane.
   */
  threadId: string | null;
  /** Finalize the stream's visible widget (e.g. into the "stopped" bubble). */
  finalize: (content: ActiveStreamContent) => void;
  /** Optional: the widget message id this entry currently targets (debug/tests). */
  messageId?: string;
}

export class ActiveStreamRegistry {
  private byThread = new Map<string | null, ActiveStreamEntry>();

  /**
   * Register (or replace) the active stream for the entry's thread.
   * A newer turn on the same thread simply overwrites the older entry —
   * same-thread sends are serialized by the pending queue, and the older
   * turn's entry-scoped `clear` can no longer evict the replacement.
   */
  register(entry: ActiveStreamEntry): ActiveStreamEntry {
    this.byThread.set(entry.threadId ?? null, entry);
    return entry;
  }

  /** Current entry for a thread (null key = legacy/single-thread lane). */
  get(threadId: string | null | undefined): ActiveStreamEntry | null {
    return this.byThread.get(threadId ?? null) ?? null;
  }

  /** True when ANY thread has a registered active stream (dormancy signal). */
  hasAny(): boolean {
    return this.byThread.size > 0;
  }

  /**
   * Clear a thread's registration. When `entry` is provided the clear is
   * entry-scoped: it only deletes when the registry still holds that exact
   * entry (a newer registration for the same thread is left untouched).
   */
  clear(threadId: string | null | undefined, entry?: ActiveStreamEntry): void {
    const key = threadId ?? null;
    if (entry && this.byThread.get(key) !== entry) return;
    this.byThread.delete(key);
  }

  /**
   * STEP 1 of cancelRunningTool: finalize-and-clear for cancellation.
   *
   *  - Explicit `threadId` (archive ×, per-chip cancel, Stop with a resolved
   *    active thread): finalizes ONLY that thread's entry. Archiving thread B
   *    can never touch thread A's live stream.
   *  - No `threadId` (`null`/`undefined` — legacy global stop, single-thread
   *    surfaces): finalizes EVERY registered entry, preserving the historical
   *    "stop everything" contract.
   *
   * Finalizers are invoked defensively — one throwing finalizer never blocks
   * the others or the abort steps that follow. Returns the number of entries
   * finalized (telemetry/tests).
   */
  finalizeForCancel(threadId: string | null | undefined, content: ActiveStreamContent): number {
    const keys: Array<string | null> = threadId === null || threadId === undefined
      ? [...this.byThread.keys()]
      : (this.byThread.has(threadId) ? [threadId] : []);
    let finalized = 0;
    for (const key of keys) {
      const entry = this.byThread.get(key);
      if (!entry) continue;
      this.byThread.delete(key);
      try {
        entry.finalize(content);
      } catch {
        // Finalize is best-effort — the widget may already be gone.
      }
      finalized += 1;
    }
    return finalized;
  }
}

/**
 * STEP 3 of cancelRunningTool: thread-scoping predicate for workflow-runtime
 * cancellation, built on the SAME runtime→thread binding Round J introduced
 * (`runtimeThreadIdByToolRef`, populated by `getOrCreateRuntime`).
 *
 *  - No `cancelThreadId` → legacy global cancel: every running runtime.
 *  - Explicit `cancelThreadId` → only runtimes BOUND to that exact thread.
 *    An UNBOUND runtime (legacy/single-thread surface, per Round J's
 *    `canRuntimeCaptureSend` rules) is never cancelled by a thread-scoped
 *    cancel — its home thread cannot be derived, and in thread-switcher mode
 *    turn-started runtimes are always bound. Thread-less surfaces resolve
 *    their Stop to `null`, which takes the global branch above.
 */
export function shouldCancelRuntimeForThread(
  runtimeBoundThreadId: string | null | undefined,
  cancelThreadId: string | null | undefined,
): boolean {
  if (cancelThreadId === null || cancelThreadId === undefined) return true;
  return (runtimeBoundThreadId ?? null) === cancelThreadId;
}
